'use client';

import { useCalendarStore } from '@/stores/calendar-store';
import { CALENDAR_SYSTEMS, getCalendarAdapter, isCalendarSystem, type CalendarSystem } from '@/lib/calendar-systems';
import { uiFb } from '@/lib/i18n';

interface CalendarSystemSelectorProps {
  /** When true, render as a tighter "bare" control without surrounding chrome (matches YearSelector). */
  bare?: boolean;
}

/**
 * Calendar-system toggle. Lets the user view the calendar in any of the 10
 * supported systems (gregorian, islamic-umalqura, hebrew, persian, buddhist,
 * ethiopic, japanese, roc, indian, bikram). Persists to localStorage; default
 * is set per-page based on lang + country (see useCalendarViewLogic).
 */
export function CalendarSystemSelector({ bare = false }: CalendarSystemSelectorProps) {
  const calendarSystem = useCalendarStore((s) => s.calendarSystem);
  const setCalendarSystem = useCalendarStore((s) => s.setCalendarSystem);
  const lang = useCalendarStore((s) => s.lang) || 'en';

  const baseClass = bare
    ? 'bg-transparent text-detail font-medium text-text-secondary outline-none focus:text-text-primary'
    : 'rounded-lg border border-border-primary bg-surface-elevated px-2.5 py-1 text-detail font-medium text-text-secondary outline-none transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-brand';

  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="sr-only">{uiFb('ui.calendar_system', 'Calendar system', lang)}</span>
      <select
        value={calendarSystem}
        onChange={(e) => {
          const next = e.target.value;
          if (isCalendarSystem(next)) setCalendarSystem(next);
        }}
        className={baseClass}
        aria-label={uiFb('ui.calendar_system', 'Calendar system', lang)}
      >
        {CALENDAR_SYSTEMS.map((id: CalendarSystem) => (
          <option key={id} value={id}>
            {getCalendarAdapter(id).displayName(lang)}
          </option>
        ))}
      </select>
    </label>
  );
}
